/**
 * Image compression utility for person images
 * Compresses large images before upload to Cloudinary
 */

import heic2any from 'heic2any';

/**
 * Check if file is HEIC format
 * @param {File} file - File to check
 * @returns {boolean} True if HEIC
 */
export const isHeicFile = (file) => {
  const name = file.name?.toLowerCase() || '';
  const type = file.type?.toLowerCase() || '';
  return name.endsWith('.heic') || name.endsWith('.heif') || 
         type === 'image/heic' || type === 'image/heif';
};

/**
 * Convert HEIC file to JPEG
 * @param {File} file - HEIC file to convert
 * @returns {Promise<File>} Converted JPEG file
 */
export const convertHeicToJpeg = async (file) => {
  if (!isHeicFile(file)) {
    return file; // Not HEIC, return as-is
  }

  console.log('🔄 Converting HEIC to JPEG...');
  
  try {
    const blob = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.9
    });

    // heic2any can return array for multi-image HEIC, take first
    const resultBlob = Array.isArray(blob) ? blob[0] : blob;
    
    const newFileName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
    const convertedFile = new File([resultBlob], newFileName, {
      type: 'image/jpeg',
      lastModified: Date.now()
    });

    console.log(`✅ HEIC converted: ${file.name} → ${newFileName}`);
    return convertedFile;
  } catch (error) {
    console.error('HEIC conversion failed:', error);
    throw new Error(`Failed to convert HEIC: ${error.message}`);
  }
};

/**
 * Process file for upload - converts HEIC and compresses if needed
 * @param {File} file - File to process
 * @param {Object} options - Compression options
 * @returns {Promise<File>} Processed file ready for upload
 */
export const processFileForUpload = async (file, options = {}) => {
  // Convert HEIC first if needed
  let processedFile = await convertHeicToJpeg(file);
  
  // Then compress if needed - target 5MB for good quality while staying well under 10MB limit
  const thresholdMB = options.thresholdMB || 5;
  if (needsCompression(processedFile, thresholdMB)) {
    processedFile = await compressImageFile(processedFile, {
      ...options,
      targetSizeMB: options.targetSizeMB || 5
    });
  }
  
  return processedFile;
};

/**
 * Compress image file to target size
 * @param {File} file - Image file to compress
 * @param {Object} options - Compression options
 * @returns {Promise<File>} Compressed image file
 */
export const compressImageFile = async (file, options = {}) => {
  const {
    maxWidth = 2048,
    maxHeight = 3072,
    targetSizeMB = 5, // Target 5MB for good quality while staying under 10MB limit
    minQuality = 0.5,
    maxQuality = 0.92
  } = options;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = async () => {
        try {
          let { width, height } = img;
          const originalSizeMB = file.size / (1024 * 1024);
          
          // Initial dimension reduction
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }

          let blob = null;
          let attempts = 0;
          const maxAttempts = 5;

          // Try progressively smaller dimensions until we hit target
          while (attempts < maxAttempts) {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);

            // Try to find optimal quality for current dimensions
            blob = await findOptimalQuality(canvas, targetSizeMB, minQuality, maxQuality);
            const sizeMB = blob.size / (1024 * 1024);

            console.log(`📦 Attempt ${attempts + 1}: ${sizeMB.toFixed(2)}MB at ${width}x${height}`);

            if (sizeMB <= targetSizeMB) {
              break; // Success!
            }

            // Reduce dimensions by 20% and try again
            width = Math.round(width * 0.8);
            height = Math.round(height * 0.8);
            attempts++;
          }

          const finalSizeMB = blob.size / (1024 * 1024);
          console.log(`✅ Compression complete: ${originalSizeMB.toFixed(2)}MB → ${finalSizeMB.toFixed(2)}MB`);

          const compressedFile = new File([blob], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now()
          });

          resolve(compressedFile);
        } catch (error) {
          reject(new Error(`Compression failed: ${error.message}`));
        }
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

/**
 * Find optimal quality for given canvas to hit target size
 */
const findOptimalQuality = async (canvas, targetSizeMB, minQuality, maxQuality) => {
  let low = minQuality;
  let high = maxQuality;
  let bestBlob = await canvasToBlob(canvas, minQuality); // Start with min quality as fallback

  for (let i = 0; i < 10; i++) {
    const quality = (low + high) / 2;
    const blob = await canvasToBlob(canvas, quality);
    const sizeMB = blob.size / (1024 * 1024);

    if (sizeMB <= targetSizeMB) {
      bestBlob = blob;
      low = quality; // Try higher quality
    } else {
      high = quality; // Need lower quality
    }

    if (high - low < 0.02) break;
  }

  return bestBlob;
};

/**
 * Helper to convert canvas to blob
 */
const canvasToBlob = (canvas, quality) => {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob'));
        }
      },
      'image/jpeg',
      quality
    );
  });
};

/**
 * Check if file needs compression
 * @param {File} file - Image file to check
 * @param {number} thresholdMB - Size threshold in MB
 * @returns {boolean} True if file needs compression
 */
export const needsCompression = (file, thresholdMB = 5) => {
  // Temporarily disabled for end-to-end testing per user request
  return false;
};

/**
 * Fetch image from URL and compress if needed
 * @param {string} imageUrl - URL of the image to fetch
 * @param {Object} options - Compression options
 * @returns {Promise<File>} Compressed image file
 */
export const fetchAndCompressImage = async (imageUrl, options = {}) => {
  const {
    maxSizeMB = 5, // Target 5MB for good quality
    maxWidth = 2048,
    maxHeight = 3072,
    minQuality = 0.6,
    maxQuality = 0.92
  } = options;

  try {
    console.log('🔄 Fetching image from URL...');
    
    // Fetch the image
    const response = await fetch(imageUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`);
    }
    
    const blob = await response.blob();
    const sizeMB = blob.size / (1024 * 1024);
    console.log(`📦 Fetched image size: ${sizeMB.toFixed(2)}MB`);
    
    // Create a File object from the blob
    const fileName = `image-${Date.now()}.jpg`;
    let file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
    
    // Only compress if over the limit
    if (sizeMB > maxSizeMB) {
      console.log(`📦 Image exceeds ${maxSizeMB}MB, compressing...`);
      
      file = await compressImageFile(file, {
        maxWidth,
        maxHeight,
        targetSizeMB: maxSizeMB,
        minQuality,
        maxQuality
      });
      
      const finalSizeMB = file.size / (1024 * 1024);
      console.log(`✅ Compressed to ${finalSizeMB.toFixed(2)}MB`);
    } else {
      console.log(`✅ Image is under ${maxSizeMB}MB, no compression needed`);
    }
    
    return file;
  } catch (error) {
    console.error('Failed to fetch and compress image:', error);
    throw error;
  }
};

/**
 * Compress image specifically for background removal
 * PNG output can be 5-10x larger than JPEG, so we need aggressive compression
 * @param {string} imageUrl - URL of the image to compress
 * @returns {Promise<File>} Compressed image file ready for BG removal
 */
export const compressForBackgroundRemoval = async (imageUrl) => {
  try {
    console.log('🔄 Checking image for background removal...');
    
    // Fetch the image
    const response = await fetch(imageUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`);
    }
    
    const blob = await response.blob();
    const originalSizeMB = blob.size / (1024 * 1024);
    console.log(`📦 Original image size: ${originalSizeMB.toFixed(2)}MB`);
    
    // Create a File object from the blob
    const fileName = `bg-removal-${Date.now()}.jpg`;
    const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
    
    // Only compress if over 1.5MB - PNG output can be 5-10x larger
    // 1.5MB JPEG -> ~7.5MB PNG worst case, stays under 10MB limit
    if (originalSizeMB <= 1.5) {
      console.log(`✅ Image is under 1.5MB, no compression needed for BG removal`);
      return file;
    }
    
    console.log(`📦 Image exceeds 1.5MB, compressing for BG removal...`);
    const compressedFile = await compressImageFile(file, {
      maxWidth: 1500,  // Limit width to prevent large PNG output
      maxHeight: 2250, // 1.5x aspect ratio
      targetSizeMB: 1.5, // Aggressive target for BG removal
      minQuality: 0.6,
      maxQuality: 0.85
    });
    
    const finalSizeMB = compressedFile.size / (1024 * 1024);
    console.log(`✅ Compressed for BG removal: ${originalSizeMB.toFixed(2)}MB → ${finalSizeMB.toFixed(2)}MB`);
    
    return compressedFile;
  } catch (error) {
    console.error('Failed to compress for background removal:', error);
    throw error;
  }
};
